/**
 * ZTracks — the internal NexusApi contract for the TRACK ITSELF and for a track's
 * LOG, MEMORY, AGENTS and EVENT STREAM.
 *
 * Endpoints: `/api/tracks` and `/api/tracks/:trackId` (the catalog), plus
 * `/api/tracks/:trackId/{diary,memory,agents,events}`, plus `/api/track-events` —
 * the organisation-wide stream, which names no track and is therefore a SIBLING
 * resource rather than a segment under `tracks/:trackId`.
 * {@link ZTracks.ListOrganizationEvents} carries why the path is not
 * `/api/tracks/events`.
 * Controllers: `tracks/infrastructure/controllers/*.controller.ts`
 *
 * ## 🔴 THE TRACK-LEVEL DESCRIPTORS REUSE THE v1 SCHEMA OBJECTS, THEY DO NOT
 * RESTATE THEM
 *
 * Every schema the catalog block below names is imported from
 * `../../public/v1/schemas/tracks.schemas` — `TrackSchema`, `ReadyTrackSchema`,
 * `ListTracksQuerySchema`, `TrackRollupsQuerySchema`, `TrackOneLineSchema`,
 * `TrackSlugSchema` and the bodies built on them. One use case, two transports:
 * these routes call the SAME use cases `V1TracksController` calls, so a second
 * statement of the rules would be a second thing to drift.
 *
 * That matters most for the ones with real predicates in them.
 * `TrackOneLineSchema` counts CODE POINTS because `char_length()` does, and its
 * own header records the 500 an unmapped SQLSTATE 23514 produced before it
 * existed; `SetTrackNextOwnerBodySchema` carries the `superRefine` that turns
 * `Track_next_owner_watcher` into a 400. Restating either here would put the
 * looser copy of a database constraint on the surface a browser reaches, which
 * is the bypass rather than a duplicate.
 *
 * ⚠️ THE DIRECTION OF THIS IMPORT IS THE PRECEDENTED ONE. `api/domains/**`
 * reading `api/public/v1/schemas/**` is what `ztickets.ts`,
 * `zanalytics-reports.ts` and
 * `zHtmlMessageTemplates.ts` already do. The reverse — a v1 descriptor reaching
 * into `api/domains/` — is the one `contract/contract-layering.test.ts` governs.
 *
 * ## 🔴 THE DIARY AND THE EVENT STREAM DECLARE NO DESTRUCTIVE VERB, AND THAT IS
 * LOAD-BEARING RATHER THAN INCOMPLETE
 *
 * There is no `DELETE`, no `PUT` and no `PATCH` on either resource — not a guarded
 * one, an ABSENT one. The donor design ships a `delete_diary_entry` store function,
 * an unguarded DELETE route and an MCP tool described as being for pruning history,
 * while three of its own documents assert the append-only guarantee holds. That
 * contradiction is not inherited.
 *
 * `apps/backend/src/__governance__/tracks-diary-is-append-only.spec.ts` reads the
 * LIVE ROUTE TABLE — Nest's own `PATH_METADATA` / `METHOD_METADATA`, not this file
 * and not a grep — and asserts the absence. It carries the memory resource's
 * `DELETE` as its positive control, so "the diary serves no DELETE" is a
 * measurement taken by an instrument proved able to see one, rather than an empty
 * population reading as a clean result.
 *
 * ## Sizes are BYTES and this contract caps nothing by length
 *
 * `TrackMemory.value` is bounded by `octet_length()` in a CHECK, and Zod's
 * `.max(n)` counts UTF-16 code units — a THIRD unit that agrees with neither bytes
 * nor characters. A 2,000-code-unit value can be 6,000 bytes of CJK. So the byte
 * budget is computed once, in `tracks/domain/value-objects/track-memory-budget.ts`,
 * and this file deliberately does not restate it as a length.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  ArchiveTrackBodySchema,
  ArchiveTrackResponseSchema,
  CreateTrackBodySchema,
  CreateTrackDependencyEdgeBodySchema,
  CreateTrackResponseSchema,
  ListReadyTracksResponseSchema,
  ListReadyTrackTasksResponseSchema,
  ListTrackRollupsResponseSchema,
  ListTrackSectionsResponseSchema,
  ListTracksQuerySchema,
  ListTracksResponseSchema,
  ListTrackTaskEdgesResponseSchema,
  ListTrackTasksResponseSchema,
  ReadySetQuerySchema,
  SetTrackNextOwnerBodySchema,
  SetTrackNextOwnerResponseSchema,
  SetTrackStatusBodySchema,
  SetTrackStatusResponseSchema,
  ToggleTrackTaskBodySchema,
  ToggleTrackTaskResponseSchema,
  TrackEdgeCreatedResponseSchema,
  TrackIdPathSchema,
  TrackRefSchema,
  TrackRollupSchema,
  TrackRollupsQuerySchema,
  TrackSchema,
  TrackTaskIdPathSchema,
  UpdateTrackCurrentStepBodySchema,
  UpdateTrackCurrentStepResponseSchema
} from "../../public/v1/schemas/tracks.schemas";

// ═══════════════════════════════════════════════════════════════
// SHARED
// ═══════════════════════════════════════════════════════════════

/**
 * The same `id`-or-`slug` spelling the v1 surface takes.
 *
 * 🔴 IT IS THE SHARED `TrackRefSchema` RATHER THAN A SECOND `.uuid()`, BECAUSE
 * BOTH SURFACES SIT BEHIND THE SAME `TrackAccessGuard`. The guard resolves a slug
 * for whichever transport reached it, so a narrower schema here would refuse at
 * the contract a value the runtime accepts — a difference between two surfaces
 * that nothing in the tree would explain and no test would catch, since each
 * surface's specs only ever read their own descriptor.
 */
const TrackPathVarsSchema = z.object({
  trackId: TrackRefSchema
});

/**
 * The members are spelled out rather than derived from `DbEnum.TrackDiaryKind`.
 *
 * The whole value of an enum in a published contract is that it is a SECOND,
 * INDEPENDENT statement of the vocabulary, so a drift check has two things to
 * compare. Deriving it from the generated enum makes the check compare a mirror
 * with itself — DRYer, and a tautology.
 */
const TrackDiaryKindSchema = z.enum([
  "PROGRESS",
  "FINDING",
  "DECISION",
  "PROOF",
  "BLOCKER",
  "QUESTION",
  "KILLED",
  "NOTE"
]);

/** Same reasoning as `TrackDiaryKindSchema`: an independent statement, not a mirror. */
const TrackAgentStateSchema = z.enum(["OPEN", "CLOSED", "DEAD", "RETIRED"]);

/** `CLOSED`, `DEAD` or `RETIRED`. `OPEN` is not a close. */
const TrackAgentCloseStateSchema = z.enum(["CLOSED", "DEAD", "RETIRED"]);

const TrackDiaryEntrySchema = z.object({
  id: z.string(),
  trackId: z.string(),
  kind: TrackDiaryKindSchema,
  body: z.string(),
  taskId: z.string().nullable(),
  agentId: z.string().nullable(),
  workspaceId: z.string().nullable(),
  artifactPath: z.string().nullable(),
  authorUserId: z.string().nullable(),
  createdAt: z.string() // ISO string
});

const TrackMemoryEntrySchema = z.object({
  id: z.string(),
  trackId: z.string(),
  key: z.string(),
  value: z.string(),
  /** UTF-8 bytes, matching `octet_length()`. NOT characters and NOT code units. */
  valueBytes: z.number().int(),
  createdAt: z.string(), // ISO string
  updatedAt: z.string() // ISO string
});

const TrackAgentSchema = z.object({
  id: z.string(),
  trackId: z.string(),
  name: z.string(),
  state: TrackAgentStateSchema,
  /** Other agents' NAMES in this track — a column, matching the donor. */
  dependsOn: z.array(z.string()),
  acceptance: z.string().nullable(),
  /** Opaque by design: what a dispatcher handed the agent. `z.unknown()`, never `z.any()`. */
  inputs: z.unknown().nullable(),
  outputPath: z.string().nullable(),
  model: z.string().nullable(),
  reason: z.string().nullable(),
  openedAt: z.string(), // ISO string
  /** The ONE heartbeat clock in this domain. The banner's staleness is derived from it and nothing else. */
  lastHeardAt: z.string(), // ISO string
  closedAt: z.string().nullable() // ISO string
});

const TrackEventSchema = z.object({
  id: z.string(),
  /** `null` for an organisation-level event with no track. */
  trackId: z.string().nullable(),
  type: z.string(),
  payload: z.unknown().nullable(),
  actorUserId: z.string().nullable(),
  actorAgentId: z.string().nullable(),
  createdAt: z.string() // ISO string
});

// ═══════════════════════════════════════════════════════════════
// BODIES
// ═══════════════════════════════════════════════════════════════

const AppendDiaryEntryBodySchema = z.object({
  kind: TrackDiaryKindSchema,
  // `.trim()` BEFORE `.min(1)`. The other order checks the length of the UNTRIMMED
  // value, so `"   "` passes — the house's 607-instance default, and it fails OPEN
  // wherever the value is later used as a substring.
  body: z.string().trim().min(1),
  taskId: z.string().uuid().nullish(),
  agentId: z.string().uuid().nullish(),
  workspaceId: z.string().uuid().nullish(),
  artifactPath: z.string().trim().min(1).nullish()
});

/**
 * A memory key is a SLUG, and the constraint is not decoration.
 *
 * The delete route addresses the key as a path segment, so a key containing `/`
 * would be unaddressable — a value that could be written and never removed, which
 * is the shape that silently consumes an 8,000-byte budget for ever. Refusing it at
 * the contract is cheaper than encoding it at four call sites and getting one wrong.
 */
const TrackMemoryKeySchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/, "A memory key is 1-128 chars of [A-Za-z0-9._-].");

const PutMemoryEntryBodySchema = z.object({
  key: TrackMemoryKeySchema,
  // NOT `.max(n)`: Zod counts UTF-16 code units, the CHECK counts BYTES, and the two
  // disagree by up to 3x on non-ASCII. `.min(1)` after `.trim()` refuses a blank,
  // which is the half a length gate genuinely does answer.
  value: z.string().trim().min(1)
});

const OpenAgentBodySchema = z.object({
  name: z.string().trim().min(1).max(128),
  dependsOn: z.array(z.string().trim().min(1)).default([]),
  acceptance: z.string().trim().min(1).nullish(),
  inputs: z.unknown().nullish(),
  outputPath: z.string().trim().min(1).nullish(),
  model: z.string().trim().min(1).nullish()
});

const CloseAgentBodySchema = z.object({
  state: TrackAgentCloseStateSchema,
  /**
   * Mandatory in practice for `DEAD` and `RETIRED`, and NOT expressed as a Zod
   * refinement.
   *
   * 🔴 THE RULE IS `char_length(btrim(translate(reason, …))) >= 15`, WHICH IS THE
   * CHECK CONSTRAINT'S OWN PREDICATE. Re-implementing it here as a `.refine()`
   * would put a THIRD statement of it in the tree — the CHECK, the domain value
   * object that mirrors it, and this — and the copy that stops matching is the one
   * that reports a clean parse over a row the database will refuse. The use case
   * owns the refusal, using the one mirror.
   */
  reason: z.string().nullish()
});

const AppendEventBodySchema = z.object({
  type: z.string().trim().min(1).max(128),
  payload: z.unknown().nullish(),
  /**
   * At least one actor. `Track_event_has_an_actor` is
   * `"actorUserId" IS NOT NULL OR "actorAgentId" IS NOT NULL`, and BOTH may be set:
   * a user acting through an agent is one event with two actors, not two events.
   *
   * `actorUserId` is NOT taken from the body when the caller is a person — the
   * controller supplies `@CurrentUserId()`. This field exists for the agent-acting
   * case and for a machine caller attributing an event to a user it names.
   */
  actorAgentId: z.string().uuid().nullish()
});

// ═══════════════════════════════════════════════════════════════
// ZTRACKS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZTracks = {
  // ═════════════════════════════════════════════════════════════
  // THE CATALOG — the track itself
  //
  // 🔴 THE DECLARATION ORDER OF THE FOUR ROOT ROUTES IS ENFORCED IN THE
  // CONTROLLER, NOT HERE. `GET /tracks/rollup`, `GET /tracks/ready`,
  // `GET /tracks/dependencies` and `GET /tracks/:trackId` are one path segment
  // each and therefore the same shape to a router; Nest matches in the order the
  // handler METHODS are declared. This manifest's key order settles nothing —
  // `tracks-catalog-route-order.spec.ts` reads the live route metadata and is
  // what actually holds it.
  // ═════════════════════════════════════════════════════════════

  /**
   * GET /api/tracks/rollup — progress for several tracks at once.
   *
   * 🔴 ONE STATEMENT, NOT N. The whole reason this route exists beside
   * {@link ZTracks.ReadRollup} is that a board rendering a page of tracks would
   * otherwise make one roll-up call per row. `trackIds` is capped at 100 by
   * {@link TrackRollupsQuerySchema} — the bound is the URL rather than the work,
   * so a full 200-track page is two calls and `1 + 2` is the honest cost.
   *
   * ⚠️ AN UNKNOWN OR FOREIGN `trackId` IS PRESENT WITH `0/0`, NEVER OMITTED. A
   * missing key would tell a caller which of the ids it sent exist in somebody
   * else's organisation.
   */
  ListRollups: {
    method: "GET" as const,
    path: "/api/tracks/rollup",
    Query: TrackRollupsQuerySchema,
    Response: ApiSuccessSchema(ListTrackRollupsResponseSchema)
  },

  /**
   * GET /api/tracks/ready — the tracks that can be worked on right now.
   *
   * Derived on every read from the dependency edges: there is no `readyAt`
   * column, nothing to invalidate and no cache. A blocker releases only at
   * `DONE`.
   */
  ListReady: {
    method: "GET" as const,
    path: "/api/tracks/ready",
    Query: ReadySetQuerySchema,
    Response: ApiSuccessSchema(ListReadyTracksResponseSchema)
  },

  /**
   * GET /api/tracks/dependencies — every track-level edge in the organisation.
   *
   * 🔴 DECLARED ABOVE {@link ZTracks.Read} IN THE CONTROLLER FOR THE SAME REASON
   * `ready` AND `rollup` ARE. A literal `dependencies` segment and a `:trackId`
   * segment are indistinguishable to the router.
   *
   * The whole edge set rather than one track's, because a board draws the graph:
   * asking per track is N reads for a set one index scan already answers, and
   * the answer would still have to be unioned client-side to find a cycle.
   */
  ListDependencyEdges: {
    method: "GET" as const,
    path: "/api/tracks/dependencies",
    Response: ApiSuccessSchema(
      z.object({
        edges: z.array(
          z.object({
            id: z.string().uuid(),
            blockerTrackId: z.string().uuid(),
            blockedTrackId: z.string().uuid()
          })
        )
      })
    )
  },

  /**
   * GET /api/tracks — what EXISTS, in `number` order, one keyset page at a time.
   *
   * 🔴 NOT THE READY SET. `ready` answers "what can be worked on" and drops
   * everything `DONE`, `BLOCKED` or archived; this answers "what exists", which
   * is the only way a caller that finished a track sees it again.
   *
   * ⚠️ THE CURSOR CARRIES THE FILTERS IT WAS ISSUED UNDER AND A MISMATCH IS A
   * 400. Round-trip `nextCursor` verbatim and never build one: replaying a
   * `status=PLANNED` cursor into a `status=DONE` request would return a page
   * that is correctly scoped, correctly counted, and STARTS IN THE MIDDLE.
   *
   * 🔴 `nextOwner` IS THE FILTER A "WAITING ON ME" PANEL HAS TO SEND. Narrowing
   * a fetched page client-side narrows ONE page of at most 200, so on any
   * organisation past that the panel silently under-reports — and it renders as
   * "nobody is waiting on you", which is the one wrong answer nobody questions.
   * `?nextOwner=USER&limit=1` also gives the badge its count, off `total`.
   *
   * `total`, `hasMore` and `nextCursor` are on the DATA object rather than the
   * envelope's `meta`, for the reason {@link ListTracksResponseSchema} records.
   */
  List: {
    method: "GET" as const,
    path: "/api/tracks",
    Query: ListTracksQuerySchema,
    Response: ApiSuccessSchema(ListTracksResponseSchema)
  },

  /**
   * POST /api/tracks — create one track. 201.
   *
   * The per-organisation `number` is allocated server-side inside the creating
   * transaction, so it is gapless; a caller neither sends it nor can predict it.
   * `.strict()` refuses it by name, along with `status` and `nextOwnerRef` —
   * {@link CreateTrackBodySchema} argues each absence.
   */
  Create: {
    method: "POST" as const,
    path: "/api/tracks",
    Body: CreateTrackBodySchema,
    Response: ApiSuccessSchema(CreateTrackResponseSchema)
  },

  /** GET /api/tracks/:trackId — one track. A foreign or absent id is 404, indistinguishably. */
  Read: {
    method: "GET" as const,
    path: "/api/tracks/:trackId",
    PathVars: TrackIdPathSchema,
    Response: ApiSuccessSchema(TrackSchema)
  },

  /**
   * POST /api/tracks/:trackId/status — move the track, and this is how one
   * finishes. 200, not 201.
   *
   * There is no delete route in this domain: the diary, the events and the
   * memory are the record of the work and all three cascade on delete. Every
   * status is reachable from every other one.
   */
  SetStatus: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/status",
    PathVars: TrackIdPathSchema,
    Body: SetTrackStatusBodySchema,
    Response: ApiSuccessSchema(SetTrackStatusResponseSchema)
  },

  /**
   * POST /api/tracks/:trackId/current-step — set, or clear, the one line. 200.
   *
   * 🔴 `currentStep` IS REQUIRED-NULLABLE RATHER THAN OPTIONAL. An optional
   * field has no spelling for "clear it": an omitted key and an explicit `null`
   * would both arrive as `undefined`.
   */
  SetCurrentStep: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/current-step",
    PathVars: TrackIdPathSchema,
    Body: UpdateTrackCurrentStepBodySchema,
    Response: ApiSuccessSchema(UpdateTrackCurrentStepResponseSchema)
  },

  /**
   * POST /api/tracks/:trackId/next-owner — say who acts next. 200.
   *
   * `nextOwnerRef` is written on every call and never merged, so moving off
   * `EVENT` clears the watcher in the same statement — which is what keeps
   * `Track_next_owner_watcher` satisfied. A ref sent with `CUE` or `USER` is a
   * 400 from the schema's own `superRefine`, rather than the 23514-as-a-500 it
   * would otherwise be.
   */
  SetNextOwner: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/next-owner",
    PathVars: TrackIdPathSchema,
    Body: SetTrackNextOwnerBodySchema,
    Response: ApiSuccessSchema(SetTrackNextOwnerResponseSchema)
  },

  /**
   * POST /api/tracks/:trackId/archive — put it away, or bring it back. 200.
   *
   * 🔴 THIS IS THE DOMAIN'S ANSWER TO DELETING A TRACK AND IT IS NOT A DELETE.
   * It is reversible with `archived: false`, and it does not touch `status`.
   */
  Archive: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/archive",
    PathVars: TrackIdPathSchema,
    Body: ArchiveTrackBodySchema,
    Response: ApiSuccessSchema(ArchiveTrackResponseSchema)
  },

  /**
   * POST /api/tracks/dependencies — declare that one track blocks another. 201.
   *
   * An edge that would close a circle is refused with 409 and the circle is
   * named in `error.labels`, in traversal order.
   */
  CreateDependency: {
    method: "POST" as const,
    path: "/api/tracks/dependencies",
    Body: CreateTrackDependencyEdgeBodySchema,
    Response: ApiSuccessSchema(TrackEdgeCreatedResponseSchema)
  },

  // ═════════════════════════════════════════════════════════════
  // THE STRUCTURE — a track's document tree and its plan
  // ═════════════════════════════════════════════════════════════

  /**
   * GET /api/tracks/:trackId/sections — the document tree, prose included.
   *
   * 🔴 `body` REACHES THE WIRE HERE FOR THE FIRST TIME. The column is writable
   * from `POST /public/v1/tracks/:trackId/sections` and no route has ever
   * returned it, so a surface rendering this tree had every title and none of
   * the content. Without it the Document tab is an outline.
   *
   * Ordered by the materialised `path`, which is a depth-first pre-order walk —
   * every parent arrives before its children. ⚠️ That is TREE order and not
   * SIBLING order: `position` decides the latter and travels on each row.
   *
   * No `limit`: a section tree is a table of contents rather than a log, and a
   * page boundary between a parent and its children hands a caller a fragment
   * it cannot build.
   */
  ListSections: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/sections",
    PathVars: TrackPathVarsSchema,
    Response: ApiSuccessSchema(ListTrackSectionsResponseSchema)
  },

  /**
   * GET /api/tracks/:trackId/tasks — the whole plan, each task with its banner.
   *
   * 🔴 `position` REACHES THE WIRE HERE FOR THE FIRST TIME, AND WITHOUT IT A
   * CLIENT ORDERS BY ARRAY ARRIVAL WITHOUT KNOWING IT IS DOING SO.
   * `@@unique([trackId, parentTaskId, position])` is the only thing that orders
   * siblings. ⚠️ It is unique per PARENT, so sorting a flat list by it alone
   * interleaves the branches — group by `parentTaskId` first.
   *
   * 🔴 THE BANNER IS FIRST ON EVERY ROW, AND IT COSTS ONE EXTRA STATEMENT FOR
   * THE WHOLE LIST RATHER THAN ONE PER TASK. The single-task read resolves the
   * holding agent per task; doing that here would be `1 + N`. The use case reads
   * the track's OPEN agents once and renders every banner against that set.
   *
   * ⚠️ A CLAIM HELD BY A CLOSED, DEAD OR RETIRED AGENT RENDERS "NOBODY IS ON
   * THIS", deliberately: a null claim and a claim held by a stopped agent both
   * mean nobody is on it now, and naming a stopped agent as working is the shape
   * the banner's own rules call worse than no banner. The banner reports the
   * CLAIM axis only — whether the task is startable is `ListReadyTasks`.
   */
  ListTasks: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/tasks",
    PathVars: TrackPathVarsSchema,
    Response: ApiSuccessSchema(ListTrackTasksResponseSchema)
  },

  /**
   * GET /api/tracks/:trackId/tasks/ready — the tasks in this track that can be
   * picked up right now.
   *
   * 🔴 NOT A FILTER OVER {@link ZTracks.ListTasks}, AND A CLIENT CANNOT DERIVE
   * IT. Readiness is an anti-join over `TrackTaskEdge` plus the roll-up's own
   * leaf rule, evaluated in SQL — a caller holding the task list has the tasks
   * and none of the edge satisfaction, so a board computing "ready" itself would
   * be a SECOND implementation of a domain rule this repo keeps in one place
   * (`tracks-rollup-has-one-implementation.spec.ts` exists for that reason).
   * {@link ZTracks.ListTaskEdges} publishes the edges for DRAWING the graph, not
   * for deciding this.
   *
   * 🔴 THE ANSWER IS A NODE STATE, NOT A LIST TO RENDER ON ITS OWN. Intersect
   * the returned ids with `ListTasks` to mark which nodes are actionable; the
   * projection here is deliberately four fields (`id`, `title`, `acceptance`,
   * `gate`) because an agent picking work needs the criterion and whether proof
   * is required, and nothing else.
   *
   * ⚠️ `gate: true` MEANS {@link ZTracks.ToggleTask} REFUSES THIS TASK WITHOUT
   * `evidence` — so a UI that ticks a gate must collect it first. The refusal is
   * a 422 and it also fires for an unevidenced gate ANYWHERE BELOW the task
   * being ticked, which this row cannot tell you about.
   *
   * Live-derived: no `readyAt` column, no cache, nothing to invalidate. `limit`
   * is clamped server-side by `clampReadySetLimit`, so an omitted one takes the
   * default page rather than the whole plan.
   *
   * ⚠️ THE v1 TWIN IS `GET /public/v1/tracks/:trackId/tasks/ready` AND BOTH CALL
   * `ListReadyTrackTasksUseCase`. One use case, two transports — a second reader
   * of this rule is how two surfaces come to disagree about what is blocked.
   */
  ListReadyTasks: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/tasks/ready",
    PathVars: TrackPathVarsSchema,
    Query: ReadySetQuerySchema,
    Response: ApiSuccessSchema(ListReadyTrackTasksResponseSchema)
  },

  /**
   * POST /api/tracks/tasks/:taskId/toggle — tick or un-tick one task. 200.
   *
   * 🔴 THE PATH NAMES A TASK AND NEVER ITS TRACK, WHICH IS WHY THIS ROUTE
   * CARRIES `@TrackRouteAccess("gated-inside-the-use-case")`.
   * `TrackAccessGuard` has no `:trackId` to read, so `ToggleTrackTaskUseCase`
   * reads the row on the base client, asserts write on the task's track, and
   * only then opens its transaction. A refusal answers the TASK's 404 rather
   * than the track's, so it cannot disclose which track a task the caller may
   * not reach belongs to.
   *
   * 🔴 `@HttpCode(OK)`. Nest answers 201 for a `@Post` unless told otherwise, and
   * 201 claims a new row is at this location — a tick creates nothing.
   *
   * ## The gate rules, which live in the use case and are inherited here
   *
   * ⚠️ A TASK MARKED `gate: true` REFUSES ITS OWN COMPLETION WITHOUT `evidence`,
   * AND SO DOES EVERY TASK ABOVE AN UNEVIDENCED GATE AT ANY DEPTH. The subtree
   * check reads `ancestorIds` rather than `parentTaskId`, so nesting does not
   * defeat it; the refusal is a 422 naming up to five blocking gates. A UI that
   * only checks the row's own `gate` flag will still see 422s it did not expect,
   * and the body is what tells it which gates to go and evidence.
   *
   * ⚠️ `evidence` IS IGNORED ON AN UN-TICK, and an un-tick CLEARS whatever
   * evidence was stored, in the same statement — there is no spelling of this
   * call that leaves proof attached to work that is not done.
   *
   * The response is `{ taskId, done }` and nothing else: the roll-up and the
   * ready set both change as a result, and both are derived, so re-reading them
   * is the honest way to learn the new state rather than trusting a payload to
   * carry a consistent snapshot of two other queries.
   */
  ToggleTask: {
    method: "POST" as const,
    path: "/api/tracks/tasks/:taskId/toggle",
    PathVars: TrackTaskIdPathSchema,
    Body: ToggleTrackTaskBodySchema,
    Response: ApiSuccessSchema(ToggleTrackTaskResponseSchema)
  },

  /**
   * GET /api/tracks/:trackId/task-edges — what blocks what, inside one plan.
   *
   * The TASK-level twin of {@link ZTracks.ListDependencyEdges}. The two sets are
   * never merged into one adjacency map: an id from one table can never appear
   * in the other, so a walk over the union would report a track-level circle
   * under whichever id it reached first.
   *
   * ⚠️ NO CYCLE INFORMATION, AND THAT IS NOT AN OMISSION. Refusing a circle is
   * the write path's job and happens inside an advisory lock over a snapshot
   * this read does not have. A caller drawing the graph gets the edges; the
   * server remains the only thing that decides whether a new one is legal.
   */
  ListTaskEdges: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/task-edges",
    PathVars: TrackPathVarsSchema,
    Response: ApiSuccessSchema(ListTrackTaskEdgesResponseSchema)
  },

  /**
   * GET /api/tracks/:trackId/rollup — leaves done, leaves total.
   *
   * 🔴 THE NUMBERS COME FROM `computeTaskRollup` AND FROM NOTHING ELSE. The donor
   * shipped an API that counted LEAVES beside a UI surface that counted TOP-LEVEL
   * GROUPS, so the same track reported two different progress numbers and neither
   * side raised an error. `computeTaskRollup` in
   * `shared/domain/tracks/task-rollup.ts` is the one implementation, and
   * `apps/backend/src/__governance__/tracks-rollup-has-one-implementation.spec.ts`
   * refuses a second one anywhere in the tree.
   *
   * There is deliberately no `percent` field. A percentage is a DISPLAY, it needs
   * a rounding rule, and a second rounding rule is the same split one layer up.
   * A caller with `done` and `total` can render whatever it likes; a caller given
   * `percent` cannot recover the counts.
   *
   * 🔴 THE SHAPE IS `TrackRollupSchema` ITSELF RATHER THAN A RESTATEMENT OF ITS
   * FIELDS, and it had to become one. This descriptor spelled `done` and `total`
   * out by hand, so when the roll-up grew `byKind` the internal client would have
   * kept parsing against a two-field object and `z.object` would have STRIPPED
   * the new key — the browser surface silently poorer than the API one, with
   * nothing red anywhere. That is the second-statement drift this file's own
   * header refuses; the restatement is now gone rather than widened.
   */
  ReadRollup: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/rollup",
    PathVars: TrackPathVarsSchema,
    Response: ApiSuccessSchema(TrackRollupSchema)
  },

  /** GET /api/tracks/:trackId/diary — the log, newest first. */
  ListDiaryEntries: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/diary",
    PathVars: TrackPathVarsSchema,
    Query: z.object({
      kind: TrackDiaryKindSchema.optional(),
      limit: z.coerce.number().int().min(1).max(200).default(50)
    }),
    Response: ApiSuccessSchema(z.object({ entries: z.array(TrackDiaryEntrySchema) }))
  },

  /**
   * POST /api/tracks/:trackId/diary — append one entry.
   *
   * 🔴 THE ONLY WRITE THIS RESOURCE HAS. There is no update and no delete twin, and
   * adding either is how the append-only guarantee is lost.
   */
  AppendDiaryEntry: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/diary",
    PathVars: TrackPathVarsSchema,
    Body: AppendDiaryEntryBodySchema,
    Response: ApiSuccessSchema(TrackDiaryEntrySchema)
  },

  /** GET /api/tracks/:trackId/memory — every entry, with the budget. */
  ListMemoryEntries: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/memory",
    PathVars: TrackPathVarsSchema,
    Response: ApiSuccessSchema(
      z.object({
        entries: z.array(TrackMemoryEntrySchema),
        /** The track's stored total in UTF-8 BYTES — `Track.memoryBytes`. */
        trackMemoryBytes: z.number().int(),
        /** `Track_memoryBytes_bounded`'s upper bound, so a caller need not hardcode it. */
        budgetBytes: z.number().int()
      })
    )
  },

  /** PUT /api/tracks/:trackId/memory — create or replace one entry, within the budget. */
  PutMemoryEntry: {
    method: "PUT" as const,
    path: "/api/tracks/:trackId/memory",
    PathVars: TrackPathVarsSchema,
    Body: PutMemoryEntryBodySchema,
    Response: ApiSuccessSchema(
      z.object({
        entry: TrackMemoryEntrySchema,
        trackMemoryBytes: z.number().int()
      })
    )
  },

  /**
   * DELETE /api/tracks/:trackId/memory/:key — remove one entry and refund its bytes.
   *
   * 🔴 THIS ROUTE IS ALSO THE POSITIVE CONTROL for
   * `tracks-diary-is-append-only.spec.ts`. That spec asserts the diary and the event
   * stream serve no `DELETE`; an assertion over a resource with no routes at all
   * would pass identically. So the spec requires the SAME instrument to find this
   * one, and "the diary has no delete" becomes a measurement rather than an empty
   * set. Deleting this route would take that spec red — correctly.
   */
  DeleteMemoryEntry: {
    method: "DELETE" as const,
    path: "/api/tracks/:trackId/memory/:key",
    PathVars: TrackPathVarsSchema.extend({
      key: TrackMemoryKeySchema
    }),
    Response: ApiSuccessSchema(
      z.object({
        deleted: z.boolean(),
        trackMemoryBytes: z.number().int()
      })
    )
  },

  /** GET /api/tracks/:trackId/agents — the agents on a track, most recently heard first. */
  ListAgents: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/agents",
    PathVars: TrackPathVarsSchema,
    Query: z.object({ state: TrackAgentStateSchema.optional() }),
    Response: ApiSuccessSchema(z.object({ agents: z.array(TrackAgentSchema) }))
  },

  /** POST /api/tracks/:trackId/agents — open one agent. The name is unique among OPEN agents. */
  OpenAgent: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/agents",
    PathVars: TrackPathVarsSchema,
    Body: OpenAgentBodySchema,
    Response: ApiSuccessSchema(TrackAgentSchema)
  },

  /** POST /api/tracks/:trackId/agents/:agentId/beat — the heartbeat. Writes `lastHeardAt` and nothing else. */
  BeatAgent: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/agents/:agentId/beat",
    PathVars: TrackPathVarsSchema.extend({ agentId: z.string().uuid() }),
    Response: ApiSuccessSchema(TrackAgentSchema)
  },

  /** POST /api/tracks/:trackId/agents/:agentId/close — leave OPEN. `DEAD`/`RETIRED` need a reason. */
  CloseAgent: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/agents/:agentId/close",
    PathVars: TrackPathVarsSchema.extend({ agentId: z.string().uuid() }),
    Body: CloseAgentBodySchema,
    Response: ApiSuccessSchema(TrackAgentSchema)
  },

  /** GET /api/tracks/:trackId/events — the stream, newest first. */
  ListEvents: {
    method: "GET" as const,
    path: "/api/tracks/:trackId/events",
    PathVars: TrackPathVarsSchema,
    Query: z.object({ limit: z.coerce.number().int().min(1).max(200).default(50) }),
    Response: ApiSuccessSchema(z.object({ events: z.array(TrackEventSchema) }))
  },

  /**
   * GET /api/track-events — the ORGANISATION'S stream, newest first, one keyset
   * page at a time.
   *
   * 🔴 THE CAPABILITY EXISTED AND HAD NO DOOR. `ListTrackEventsPort` documents
   * `trackId: null` as the whole organisation's stream and the index that serves
   * it is in the schema, but both event controllers were mounted under
   * `tracks/:trackId` and parsed a track id off the path before calling — so
   * nothing could ever produce the `null`.
   *
   * 🔴 A SIBLING PATH, NOT `/api/tracks/events`. Those two are the same shape to
   * a router as `/api/tracks/:trackId`, and the collision would be settled by the
   * order two different CONTROLLERS are registered in a module — invisible to any
   * per-controller declaration-order check. A distinct first segment removes the
   * collision rather than ordering around it.
   *
   * 🔴 A CURSOR RATHER THAN AN OFFSET, AND ON THIS STREAM THAT IS FORCED. The
   * table is append-only and this read is newest-first, so rows arrive at the head
   * of the page a reader is walking: `OFFSET n` counts from the head, so every
   * event appended between two calls shifts the window — the reader re-reads rows
   * and silently loses others. `nextCursor` is anchored to the last ROW delivered,
   * which an insert above it cannot move.
   *
   * ⚠️ NO `total`, deliberately. It is a `COUNT(*)` over an append-only
   * high-volume table, stale before the response is written.
   */
  ListOrganizationEvents: {
    method: "GET" as const,
    path: "/api/track-events",
    Query: z.object({
      limit: z.coerce.number().int().min(1).max(200).default(50),
      /**
       * Round-trip `nextCursor`; never build one, and never replay one under
       * different filters — a cursor is a position inside a filtered set, so that
       * is a 400 rather than a page that silently starts in the middle.
       */
      cursor: z.string().trim().min(1).optional(),
      /**
       * Inclusive lower bound on `createdAt`, full ISO-8601.
       *
       * NOT `z.coerce.date()`: coercion is `new Date(value)`, which reads `"2"` as
       * February 2001 — a mistyped bound would silently return a different window
       * with no error anywhere.
       */
      since: z.string().datetime({ offset: true }).optional(),
      /** Exact `TrackEvent.type`. Omitted returns every type. */
      type: z.string().trim().min(1).max(128).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        events: z.array(TrackEventSchema),
        /** `null` at the end of the stream. A FULL page always carries one, even when it was the last. */
        nextCursor: z.string().nullable()
      })
    )
  },

  /** POST /api/tracks/:trackId/events — append one event. Every event names an actor. */
  AppendEvent: {
    method: "POST" as const,
    path: "/api/tracks/:trackId/events",
    PathVars: TrackPathVarsSchema,
    Body: AppendEventBodySchema,
    Response: ApiSuccessSchema(TrackEventSchema)
  }
} as const;
